<script setup lang="ts">
interface MaskShape {
  id: string
  filename: string
  url: string
  width: number
  height: number
  svgContent?: string | null
}

interface Props {
  maskShape: MaskShape
  text: string
  corner?: 'top-left' | 'bottom-left'
  /** Any CSS colour; defaults to the page accent. */
  backgroundColor?: string | null
  /** Fill while the closest `.group` ancestor is hovered. Omit for no hover state. */
  hoverColor?: string | null
  /** Any CSS colour for the resting label. Omit to pick black or white by contrast. */
  textColor?: string | null
  /** Same, for the label while hovered. Only read when `hoverColor` is set. */
  hoverTextColor?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  corner: 'top-left',
  backgroundColor: null,
  hoverColor: null,
  textColor: null,
  hoverTextColor: null,
})

const svgWrapper = ref<HTMLElement | null>(null)
const textRect = ref<CornerRect | null>(null)

const backgroundColor = computed(() => props.backgroundColor || MASK_ACCENT)

// An explicit text colour is the caller's call, per state. Otherwise
// `currentColor` for a background we can neither measure nor resolve through
// the palette convention — inheriting beats picking a side at random.
const textColor = computed(() =>
  props.textColor || contrastTextColor(backgroundColor.value, { fallback: 'currentColor' }),
)
const hoverTextColor = computed(() => {
  if (!props.hoverColor) return null
  return props.hoverTextColor || contrastTextColor(props.hoverColor, { fallback: 'currentColor' })
})

const viewBox = computed(() => {
  if (props.maskShape.svgContent) {
    const parsed = parseSvgViewBox(props.maskShape.svgContent)
    if (parsed) return parsed
  }
  return { x: 0, y: 0, width: props.maskShape.width, height: props.maskShape.height }
})

const maskStyle = computed(() => {
  return {
    ...useMaskStyle(props.maskShape),
    // Only the rest/hover *inputs* go inline. The `--mask-bg` / `--mask-fg`
    // the fill layer and the text actually read are picked between them in the
    // stylesheet below, since an inline declaration would outrank the hover
    // rule and pin them to their resting values.
    '--mask-bg-rest': backgroundColor.value,
    '--mask-fg-rest': textColor.value,
    '--mask-bg-hover': props.hoverColor,
    '--mask-fg-hover': hoverTextColor.value,
  }
})

// The svg no longer paints anything — the masked fill layer does — but it stays
// in the box as the geometry the text rect is measured against and as the hit
// region (see the pointer-events rules). The source svg only ever carries a
// viewBox (no width/height attributes), so left to its own devices the browser
// sizes it independently from the container's aspect-ratio box — any rounding
// drift between those two separate layout calculations shifts the geometry out
// from under the percentage-based text rect. Forcing it to fill the exact same
// box (with preserveAspectRatio="none", since the container's aspect ratio
// already matches the viewBox) keeps both measured against one coordinate frame.
function sizeSvgToBox() {
  const svg = svgWrapper.value?.querySelector('svg')
  if (!svg) return
  svg.setAttribute('width', '100%')
  svg.setAttribute('height', '100%')
  svg.setAttribute('preserveAspectRatio', 'none')
}

function updateTextRect() {
  const shape = svgWrapper.value?.querySelector<SVGGeometryElement>('path, polygon, rect, circle, ellipse, polyline, line')
  const { width, height } = viewBox.value
  if (!shape || !width || !height) {
    textRect.value = null
    return
  }
  textRect.value = findCornerRect(shape, viewBox.value, props.corner) ?? null
}

onMounted(async () => {
  await nextTick()
  sizeSvgToBox()
  updateTextRect()
})

watch([() => props.maskShape.svgContent, () => props.corner], async () => {
  await nextTick()
  sizeSvgToBox()
  updateTextRect()
})

const textStyle = computed(() => {
  if (!textRect.value) return { display: 'none' }
  const { left, top, width, height, textAlign, verticalAlign } = textRect.value
  return {
    left: `${left}%`,
    top: `${top}%`,
    width: `${width}%`,
    height: `${height}%`,
    textAlign,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: verticalAlign === 'bottom' ? 'flex-end' : 'flex-start',
  }
})
</script>

<template>
  <div
    class="mask-shape relative"
    :class="{ 'mask-shape--hoverable': hoverColor }"
    :style="maskStyle"
  >
    <div class="mask-shape__fill absolute inset-0" />
    <div ref="svgWrapper" class="absolute inset-0" v-html="maskShape.svgContent" />
    <div
      v-if="textRect"
      class="absolute p-1 text-lg mask-shape__text"
      :style="textStyle"
    >
      {{ text }}
    </div>
  </div>
</template>

<style scoped>
/* Both the fill and the text read from these vars, so the hover rule only has
   to swap the pair — and it can, because neither is set inline on the elements
   that consume them. */
.mask-shape {
  --mask-bg: var(--mask-bg-rest);
  --mask-fg: var(--mask-fg-rest);

  color: var(--mask-fg);
}

.mask-shape__fill {
  background: var(--mask-bg);
}

/* Transparent rather than `none`: `none` would drop the geometry out of
   hit-testing under `visiblePainted` below. Set here rather than on the markup
   so it also wins over a fill a source shape happens to carry — an author
   stylesheet always beats a presentation attribute. */
.mask-shape :deep(svg) {
  fill: transparent;
}

.group:hover .mask-shape--hoverable {
  --mask-bg: var(--mask-bg-hover);
  --mask-fg: var(--mask-fg-hover);
}

/* Only the painted artwork (and the label sitting inside it) answers the
   pointer — the rest of the aspect-ratio box lets events through, so a
   wrapping link is hovered and clicked on the shape rather than on the
   rectangle around it. Ancestors of the painted hit still match `:hover`,
   which is what keeps the `.group:hover` rule above working; those ancestors
   need `pointer-events: none` of their own so their boxes don't catch first. */
.mask-shape,
.mask-shape__fill,
.mask-shape :deep(svg) {
  pointer-events: none;
}

/* `auto` resolves to `visiblePainted` inside an svg, i.e. the filled geometry. */
.mask-shape :deep(svg *),
.mask-shape__text {
  pointer-events: auto;
}
</style>
